import type { ApiResult } from '../types';
import type { CivilAppellateWizardDraft } from '../types/civilAppellateCaseRequest';
import type { CivilAppellateStepsResponse } from '../types/civilAppellateApi';
import type { DocumentChecklistItem } from '../mocks/criminalMasterData';
import {
  isCivilAppellateCategory,
  setCivilAppellateCategoryId,
  type CivilAppellateWizardStep,
} from '../pages/wizard/civilAppellate/civilAppellateConstants';
import { DEFAULT_CIVIL_APPELLATE_DRAFT } from '../layouts/CivilAppellateRequestLayout';
import { caseApiRequest } from './apiClient';
import {
  caseRequestStepsPath,
  type CaseRequestViewOptions,
} from './caseRequestViewOptions';
import * as criminalService from './criminalCaseRequests';
import {
  buildSaveCivilAppellatePayload,
  caseRequestIdFromResponse,
  mapCivilAppellateStepsToDraft,
  referenceNoFromResponse,
  resolveCivilAppellateDraftResumeStep,
} from './civilAppellateCaseMapper';

export const getFileCategories = criminalService.getFileCategories;

/** Resolve Civil Appellate category id from master data when available. */
export async function resolveCivilAppellateCategoryId(): Promise<string> {
  const res = await getFileCategories();
  if (!res.success) return '';
  const match = res.data.find((c) => isCivilAppellateCategory(c));
  if (match?.id) setCivilAppellateCategoryId(match.id);
  return match?.id ?? '';
}
export const getSubFileCategories = criminalService.getSubFileCategories;
export const getSubCategoryApplicableAreas = criminalService.getSubCategoryApplicableAreas;
export const getProvinces = criminalService.getProvinces;
export const getDistricts = criminalService.getDistricts;
export const getUrgencyLevels = criminalService.getUrgencyLevels;
export const getDeliveryMethods = criminalService.getDeliveryMethods;
export const getCorporateInstitutes = criminalService.getCorporateInstitutes;
export const getDocumentChecklist = criminalService.getDocumentChecklist;
export const uploadCaseFile = criminalService.uploadCaseFile;
export const downloadCaseFile = criminalService.downloadCaseFile;
export {
  getCommonCoverNote as getCivilAppellateCoverNote,
  downloadCommonCoverNotePdf as downloadCivilAppellateCoverNotePdf,
} from './coverNoteRequests';

export type SaveCivilAppellateResult = {
  requestId: string;
  fileReferenceNumber?: string;
};

/** CMS-CACR-1 — save/update civil appellate steps 1–5 */
export async function saveCivilAppellateRequest(
  draft: CivilAppellateWizardDraft,
  options: {
    isDraft: boolean;
    currentStep?: CivilAppellateWizardStep;
    allSteps?: boolean;
    confirmedCorrect?: boolean;
    isUpdateAlreadySubmitted?: boolean;
  },
): Promise<ApiResult<SaveCivilAppellateResult>> {
  const body = buildSaveCivilAppellatePayload(draft, {
    isDraft: options.isDraft,
    currentStep: options.currentStep,
    allSteps: options.allSteps,
    confirmedCorrect: options.confirmedCorrect,
    isUpdateAlreadySubmitted: options.isUpdateAlreadySubmitted,
  });

  const res = await caseApiRequest<CivilAppellateStepsResponse>('/civil-appellate/steps/save', {
    method: 'POST',
    body,
  });

  if (!res.ok) return { success: false, message: res.message };

  const requestId = caseRequestIdFromResponse(res.data) || draft.draftRequestId;

  return {
    success: true,
    data: {
      requestId,
      fileReferenceNumber: referenceNoFromResponse(res.data),
    },
  };
}

export type CivilAppellateRequestViewData = {
  draft: CivilAppellateWizardDraft;
  statusName: string;
  submittedAt: string;
  completionRemarks?: string;
  step1?: CivilAppellateStepsResponse['step_1'];
  step2?: CivilAppellateStepsResponse['step_2'];
  step3?: CivilAppellateStepsResponse['step_3'];
  step4?: CivilAppellateStepsResponse['step_4'];
  step5?: CivilAppellateStepsResponse['step_5'];
};

/** CMS-CACR-2 — full GET payload (wizard resume + view) */
export async function getCivilAppellateRequestViewData(
  caseRequestId: string,
  options?: CaseRequestViewOptions,
): Promise<ApiResult<CivilAppellateRequestViewData | null>> {
  if (!caseRequestId.trim()) return { success: true, data: null };

  const res = await caseApiRequest<CivilAppellateStepsResponse>(
    caseRequestStepsPath(`/civil-appellate/steps/${encodeURIComponent(caseRequestId)}`, options),
    {
      ...(options?.signal ? { signal: options.signal } : {}),
    },
  );
  if (!res.ok) return { success: false, message: res.message };

  const draft = mapCivilAppellateStepsToDraft(res.data, {
    ...DEFAULT_CIVIL_APPELLATE_DRAFT,
    draftRequestId: caseRequestIdFromResponse(res.data) || caseRequestId,
  });

  const statusName = (res.data.status_name ?? res.data.status_code ?? '').trim();
  const submittedAt = (res.data.created_at ?? res.data.updated_at ?? res.data.updatedAt ?? '').trim();
  const raw = res.data as { completion_remarks?: string };

  return {
    success: true,
    data: {
      draft,
      statusName,
      submittedAt,
      completionRemarks: typeof raw.completion_remarks === 'string' ? raw.completion_remarks : undefined,
      step1: res.data.step_1,
      step2: res.data.step_2,
      step3: res.data.step_3,
      step4: res.data.step_4,
      step5: res.data.step_5,
    },
  };
}

/** CMS-CACR-2 — load saved civil appellate steps for continue draft / wizard resume */
export async function getCivilAppellateRequestDetails(
  caseRequestId: string,
): Promise<ApiResult<CivilAppellateWizardDraft | null>> {
  const view = await getCivilAppellateRequestViewData(caseRequestId);
  if (!view.success) return { success: false, message: view.message };
  return { success: true, data: view.data?.draft ?? null };
}

/** CMS-CACR-2 — which wizard step to open when continuing a saved draft */
export async function getCivilAppellateDraftResumeStep(
  caseRequestId: string,
): Promise<ApiResult<{ step: CivilAppellateWizardStep; requestId: string }>> {
  if (!caseRequestId.trim()) return { success: false, message: 'Case request id is missing.' };

  const res = await caseApiRequest<CivilAppellateStepsResponse>(
    `/civil-appellate/steps/${encodeURIComponent(caseRequestId)}`,
  );
  if (!res.ok) return { success: false, message: res.message };

  const requestId = caseRequestIdFromResponse(res.data) || caseRequestId;
  return {
    success: true,
    data: { step: resolveCivilAppellateDraftResumeStep(res.data), requestId },
  };
}

export async function submitCivilAppellateRequest(
  draft: CivilAppellateWizardDraft,
): Promise<ApiResult<SaveCivilAppellateResult>> {
  return saveCivilAppellateRequest(draft, {
    isDraft: false,
    allSteps: true,
    confirmedCorrect: draft.confirmCorrect,
  });
}

export type { DocumentChecklistItem };
